import React, { useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack, Typography } from '@mui/material';

import type { MediaImage, PropertyRoomTypeWithImages } from '../ImagesVideo';
import { ROOM_IMAGE_LIMIT } from '../utils';

import RoomImagePickerDialog from './PickerDialog';
import RoomImageSlot from './Slot';
import type { RoomImage, RoomImagesByKey } from './utils';

interface Props {
  hiddenImagePublicIds: Array<string>;
  mediaImages: Array<MediaImage>;
  onAssignImagesToRoom: (roomName: string, selectedImages: Array<MediaImage>) => void;
  onRemoveImageFromRoom: (publicId: string) => void;
  onToggleHiddenImage: (publicId: string) => void;
  room: PropertyRoomTypeWithImages;
  roomImages: Array<RoomImage>;
  roomKey: string;
  setRoomImagesByKey: React.Dispatch<React.SetStateAction<RoomImagesByKey>>;
}

export default function ImagesByRoomRow({
  hiddenImagePublicIds,
  mediaImages,
  onAssignImagesToRoom,
  onRemoveImageFromRoom,
  onToggleHiddenImage,
  room,
  roomImages,
  roomKey,
  setRoomImagesByKey,
}: Props) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleAddImages = (selectedImages: Array<MediaImage>) => {
    onAssignImagesToRoom(room.name || 'Room', selectedImages);
    setRoomImagesByKey((currentRoomImagesByKey) => {
      const selectedPublicIds = new Set(selectedImages.map((image) => image.publicId));
      const nextRoomImagesByKey = Object.fromEntries(
        Object.entries(currentRoomImagesByKey).map(([currentRoomKey, currentRoomImages]) => [
          currentRoomKey,
          currentRoomKey === roomKey
            ? currentRoomImages
            : currentRoomImages.filter((image) => !selectedPublicIds.has(image.publicId)),
        ]),
      );
      const currentRoomImages = nextRoomImagesByKey[roomKey] ?? [];
      const selectedRoomImages = selectedImages.map((image) => ({
        publicId: image.publicId,
        title: image.title,
      }));
      const existingPublicIds = new Set(currentRoomImages.map((image) => image.publicId));
      const newRoomImages = selectedRoomImages.filter((image) => !existingPublicIds.has(image.publicId));

      return {
        ...nextRoomImagesByKey,
        [roomKey]: currentRoomImages.concat(newRoomImages).slice(0, ROOM_IMAGE_LIMIT),
      };
    });
  };

  const handleDeleteImage = (publicId: string) => {
    onRemoveImageFromRoom(publicId);
    setRoomImagesByKey((currentRoomImagesByKey) => ({
      ...currentRoomImagesByKey,
      [roomKey]: (currentRoomImagesByKey[roomKey] ?? []).filter((image) => image.publicId !== publicId),
    }));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setRoomImagesByKey((currentRoomImagesByKey) => {
      const currentRoomImages = currentRoomImagesByKey[roomKey] ?? [];
      const oldIndex = currentRoomImages.findIndex((image) => image.publicId === active.id);
      const newIndex = currentRoomImages.findIndex((image) => image.publicId === over.id);

      if (oldIndex < 0 || newIndex < 0) {
        return currentRoomImagesByKey;
      }

      return {
        ...currentRoomImagesByKey,
        [roomKey]: arrayMove(currentRoomImages, oldIndex, newIndex),
      };
    });
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '220px minmax(0, 1fr)' },
        gap: { xs: 2, lg: 3 },
        py: 3,
      }}
    >
      <Stack spacing={1.5} alignItems="flex-start">
        <Box>
          <Typography variant="h6">{room.name || 'Room'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {roomImages.length} of {ROOM_IMAGE_LIMIT} slots filled
          </Typography>
        </Box>
        <Button
          variant="text"
          color="primary"
          startIcon={<AddIcon />}
          disabled={roomImages.length >= ROOM_IMAGE_LIMIT || mediaImages.length === 0}
          onClick={() => setIsPickerOpen(true)}
        >
          Pick images
        </Button>
      </Stack>

      <DndContext onDragEnd={handleDragEnd} sensors={sensors} collisionDetection={closestCenter}>
        <SortableContext items={roomImages.map((image) => image.publicId)} strategy={rectSortingStrategy}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))',
                xl: `repeat(${ROOM_IMAGE_LIMIT}, minmax(0, 1fr))`,
              },
              gap: 2,
            }}
          >
            {Array.from({ length: ROOM_IMAGE_LIMIT }, (_value, index) => (
              <RoomImageSlot
                key={roomImages[index]?.publicId ?? index}
                hiddenImagePublicIds={hiddenImagePublicIds}
                image={roomImages[index]}
                onDeleteImage={handleDeleteImage}
                onOpenPicker={() => setIsPickerOpen(true)}
                onToggleHiddenImage={onToggleHiddenImage}
                slotNumber={index + 1}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>
      <RoomImagePickerDialog
        mediaImages={mediaImages}
        open={isPickerOpen}
        roomImages={roomImages}
        roomName={room.name || 'Room'}
        onAddImages={handleAddImages}
        onClose={() => setIsPickerOpen(false)}
      />
    </Box>
  );
}
